/**
 * Copy ISIN Button Component
 *
 * A reusable button that copies an ISIN code to the clipboard.
 * Features:
 * - Direct clipboard API for standalone use
 * - iFrame bridge via postMessage for embedded scenarios
 * - Toast notification on success/failure
 * - Visual feedback during copy operation
 */

import * as React from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Button, type buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { VariantProps } from "class-variance-authority";

// ============================================================================
// Types
// ============================================================================

export interface CopyISINButtonProps
  extends Omit<React.ComponentProps<"button">, "onClick">,
    VariantProps<typeof buttonVariants> {
  /** The ISIN code to copy */
  isin: string;
  /** Optional label to show alongside the icon */
  showLabel?: boolean;
  /** Custom label text (default: "Copy ISIN") */
  label?: string;
  /** Callback fired after successful copy */
  onCopySuccess?: (isin: string) => void;
  /** Callback fired after copy failure */
  onCopyError?: (error: Error) => void;
  /** Whether to use asChild pattern */
  asChild?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

/** Duration in ms to show the success checkmark */
const SUCCESS_FEEDBACK_DURATION = 2000;

/** Message type for iFrame bridge communication */
const IFRAME_COPY_MESSAGE_TYPE = "CURIE_COPY_TO_CLIPBOARD";

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if the current window is running inside an iframe
 */
function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // If we can't access window.top due to cross-origin restrictions,
    // we're definitely in an iframe
    return true;
  }
}

/**
 * Copy text to clipboard using the modern Clipboard API
 */
async function copyToClipboardDirect(text: string): Promise<void> {
  if (!navigator.clipboard) {
    throw new Error("Clipboard API not available");
  }
  await navigator.clipboard.writeText(text);
}

/**
 * Copy text via iFrame bridge using postMessage
 * The parent window should listen for this message and handle the copy
 */
function copyViaIframeBridge(text: string, messageType: string): void {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(
      {
        type: messageType,
        payload: { text },
      },
      "*" // In production, specify the exact origin
    );
  }
}

/**
 * Fallback copy method using execCommand (deprecated but widely supported)
 */
function copyToClipboardFallback(text: string): void {
  const textArea = document.createElement("textarea");
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.cssText =
    "position:fixed;top:0;left:0;width:2em;height:2em;padding:0;border:none;outline:none;box-shadow:none;background:transparent;";

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand("copy");
    if (!successful) {
      throw new Error("execCommand copy failed");
    }
  } finally {
    document.body.removeChild(textArea);
  }
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * CopyISINButton
 *
 * A button component that copies an ISIN code to the clipboard with
 * visual feedback and toast notifications.
 *
 * @example
 * ```tsx
 * // Icon-only button (default)
 * <CopyISINButton isin="INF200K01RJ1" />
 *
 * // With label
 * <CopyISINButton isin="INF200K01RJ1" showLabel />
 *
 * // Custom styling
 * <CopyISINButton
 *   isin="INF200K01RJ1"
 *   variant="outline"
 *   size="sm"
 *   showLabel
 *   label="Copy"
 * />
 *
 * // With callbacks
 * <CopyISINButton
 *   isin="INF200K01RJ1"
 *   onCopySuccess={(isin) => console.log(`Copied: ${isin}`)}
 *   onCopyError={(error) => console.error(error)}
 * />
 * ```
 */
export function CopyISINButton({
  isin,
  showLabel = false,
  label = "Copy ISIN",
  variant = "ghost",
  size = "icon-sm",
  className,
  onCopySuccess,
  onCopyError,
  disabled,
  ...props
}: CopyISINButtonProps) {
  const [copied, setCopied] = React.useState(false);
  const [copying, setCopying] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent event bubbling (e.g., when button is inside a clickable card)
      e.stopPropagation();
      e.preventDefault();

      if (copying || !isin) return;

      setCopying(true);

      try {
        // If in iframe, try bridge method first, then fallback to direct copy
        if (isInIframe()) {
          // Send message to parent for copy
          copyViaIframeBridge(isin, IFRAME_COPY_MESSAGE_TYPE);

          // Also try direct copy as backup (may work in same-origin iframes)
          try {
            await copyToClipboardDirect(isin);
          } catch {
            // Silent fail - parent should handle via bridge
            copyToClipboardFallback(isin);
          }
        } else {
          // Direct copy for standalone use
          try {
            await copyToClipboardDirect(isin);
          } catch {
            // Fallback for older browsers
            copyToClipboardFallback(isin);
          }
        }

        // Success feedback
        setCopied(true);
        toast.success("ISIN copied to clipboard", {
          description: isin,
          duration: 3000,
        });
        onCopySuccess?.(isin);

        // Reset success state after delay
        timeoutRef.current = setTimeout(() => {
          setCopied(false);
        }, SUCCESS_FEEDBACK_DURATION);
      } catch (error) {
        const err =
          error instanceof Error ? error : new Error("Failed to copy ISIN");
        toast.error("Failed to copy ISIN", {
          description: "Please try again or copy manually",
          duration: 3000,
        });
        onCopyError?.(err);
      } finally {
        setCopying(false);
      }
    },
    [isin, copying, onCopySuccess, onCopyError]
  );

  // Determine button size based on whether label is shown
  const buttonSize = showLabel ? (size === "icon-sm" ? "sm" : size) : size;

  return (
    <Button
      type="button"
      variant={variant}
      size={buttonSize}
      className={cn(
        "transition-colors",
        copied && "text-green-600 dark:text-green-400",
        className
      )}
      onClick={handleCopy}
      disabled={disabled || copying || !isin}
      aria-label={copied ? "ISIN copied" : label}
      title={`${label}: ${isin}`}
      {...props}
    >
      {copied ? (
        <Check className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Copy className="h-4 w-4" aria-hidden="true" />
      )}
      {showLabel && <span>{copied ? "Copied!" : label}</span>}
    </Button>
  );
}

export default CopyISINButton;
